// src/feed/visibilityCap.ts
//
// Visibility-cap helpers for derived feed items.
//
// CONSENT INVARIANT: an item derived from another (shared_reaction
// today; future repost-style kinds tomorrow) cannot exceed its
// source's visibility. The original author owns the audience
// choice forever — no downstream user can elevate it. This is
// what every consent-based social system does (Instagram, X,
// Threads, Bluesky, TikTok all enforce it).
//
// Derived items carry `source_visibility` as a snapshot of the
// source's visibility. The cap is the ceiling; downgrades are
// always allowed.
//
// Server enforcement lives in firestore.rules + the reactions
// cascade Cloud Function. This module is for UI gating —
// disabling menu options, showing helpful tooltips. The rules
// remain authoritative; never trust the UI alone.

import { FeedVisibility } from './types';

/** Visibility rank — higher is broader audience. */
export const VISIBILITY_RANK: Record<FeedVisibility, number> = {
  private: 0,
  followers: 1,
  public: 2,
};

/** Take the lower of two visibilities — standard cap operation. */
export function capVisibility(
  chosen: FeedVisibility,
  ceiling: FeedVisibility
): FeedVisibility {
  return VISIBILITY_RANK[chosen] <= VISIBILITY_RANK[ceiling]
    ? chosen
    : ceiling;
}

/** Whether `target` is allowed given the cap `ceiling`. When
 *  `ceiling` is undefined (item is not derived), all targets are
 *  allowed. */
export function isVisibilityAllowed(
  target: FeedVisibility,
  ceiling: FeedVisibility | undefined
): boolean {
  if (!ceiling) return true;
  return VISIBILITY_RANK[target] <= VISIBILITY_RANK[ceiling];
}

/** Sharing requires the item to be allowed to reach anyone with
 *  the link — i.e. the source must be public. If the source is
 *  followers-only or private, share-link minting is forbidden. */
export function isShareAllowed(
  ceiling: FeedVisibility | undefined
): boolean {
  if (!ceiling) return true;
  return ceiling === 'public';
}

/** Human-readable label for the cap reason — used in tooltips. */
export function capExplanation(ceiling: FeedVisibility): string {
  switch (ceiling) {
    case 'private':
      return 'Original is private — this can only stay private.';
    case 'followers':
      return "Original is followers-only — this can't be made public.";
    case 'public':
      return '';
  }
}
